import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Editor } from 'tinymce';
import { IRichContentEditorState } from '../../../extViewmodels';
import { useErrorMessages } from '../../../models/hooks/appStateHooks';
import { AttachInputButton } from '../../components/AttachInputButton';
import { DeprecatedCloseButton } from '../../components/DeprecatedCloseButton';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { TextCampaignEditor } from '../../components/TextCampaignEditor';
import { EmojiPickerButton } from '../../components/texting/EmojiPickerButton';
import { MergeFieldsDropdown } from '../../components/texting/MergeFieldsDropdown';
import { OutboundTextMessageBubble } from '../../components/texting/MessageBubble/OutboundTextMessageBubble';
import { brandPrimary, navigation, oldSilver, urgentDate } from '../../styles/colors';
import { FileImagePreview } from './FileImagePreview';
import { StoredAttachmentPreview } from './StoredAttachmentPreview';
import { styleSheet } from './styles';

const TEXT_CAMPAIGN_MAX_CHARACTERS = 1600;

export function TextCampaignPageBody({
	contentEditorState,
	attachmentFiles,
	storedAttachments,
	onAttachFiles,
	onRemoveAttachmentFile,
	onContentStateChanged,
	onRemoveStoredAttachment,
}: {
	attachmentFiles: File[];
	storedAttachments: Api.IFileAttachment[];
	onAttachFiles: (files: File[]) => void;
	onRemoveAttachmentFile: (file: File) => void;
	onRemoveStoredAttachment: (attachmentId: string) => void;
	contentEditorState: IRichContentEditorState;
	onContentStateChanged: (content: IRichContentEditorState) => void;
}) {
	const contentEditorRef = React.useRef<Editor>();
	const [displayedCharacterCount, setDisplayedCharacterCount] = React.useState(0);
	const errorMessages = useErrorMessages();
	const hasAttachments = attachmentFiles.length > 0 || storedAttachments.length > 0;

	React.useEffect(() => {
		const textContent = contentEditorState?.getPlainTextPreview(false);

		if (textContent) {
			setDisplayedCharacterCount(textContent.length);
		}
	}, [contentEditorState]);

	const isOverLimit = displayedCharacterCount > TEXT_CAMPAIGN_MAX_CHARACTERS;
	return (
		<>
			{/* Main container with toolbar and editor */}
			<div className={css(styleSheet.editorContainer, hasAttachments && styleSheet.editorContainerWithAttachments)}>
				{/* Toolbar row inside the container */}
				<div className={css(styleSheet.toolbar)}>
					<div className={css(styleSheet.toolbarLeft)}>
						<EmojiPickerButton
							onEmojiClick={(_, data) => {
								contentEditorRef.current?.insertContent(data.emoji);
							}}
						/>
						<div className={css(styleSheet.toolbarDivider)} />
						<AttachInputButton
							multiple={true}
							onChange={ev => {
								if (!ev.target.files) {
									return;
								}
								const files = Array.from(ev.target.files).filter(file =>
									attachmentFiles.every(f => f.name !== file.name)
								);
								let totalFileSize = 0;
								for (const attachedFile of attachmentFiles) {
									totalFileSize += attachedFile.size;
								}
								for (const file of files) {
									totalFileSize += file.size;
								}
								ev.target.value = null;
								if (totalFileSize > Api.TextMessagingMaxFileByteSize) {
									errorMessages.push({
										messages: ['Total attached file size must not exceed 4.5MB.'],
									});
									return;
								}
								onAttachFiles(files);
							}}
						/>
						<div className={css(styleSheet.toolbarDivider)} />
						<MergeFieldsDropdown editorRef={contentEditorRef} />
						<div className={css(styleSheet.toolbarDivider)} />
					</div>
					<span
						className={css(styleSheet.characterCount)}
						style={{
							color: isOverLimit ? urgentDate : navigation,
							fontWeight: isOverLimit ? 'bold' : 'normal',
						}}
					>
						{displayedCharacterCount.toLocaleString()} / {TEXT_CAMPAIGN_MAX_CHARACTERS.toLocaleString()}
					</span>
				</div>
				<div className={css(styleSheet.editorWrapper)}>
					<TextCampaignEditor
						onLoad={editor => {
							contentEditorRef.current = editor;
						}}
						onContentStateChanged={onContentStateChanged}
						contentState={contentEditorState}
					/>
				</div>
			</div>
			{/* Attachments section */}
			{hasAttachments ? (
				<div className={css(styleSheet.attachmentsContainer)}>
					{storedAttachments.map(attachment => {
						return (
							<StoredAttachmentPreview
								attachment={attachment}
								key={attachment.url}
								onClose={() => onRemoveStoredAttachment(attachment.id)}
							/>
						);
					})}
					{attachmentFiles.map((file, i) => {
						return file.type.includes('image/') ? (
							<div className={css(styleSheet.imageAttachmentWrapper)} key={i}>
								<FileImagePreview
									className={css(styleSheet.attachmentImagePreview)}
									file={file}
									loadingEl={<LoadingSpinner type='extra-small' />}
								/>
								<DeprecatedCloseButton
									className={css(styleSheet.attachmentCloseButton)}
									fillColor={oldSilver}
									onClick={() => {
										onRemoveAttachmentFile(file);
									}}
									opacity={0.9}
									outlineColor='#FFF'
									outlineWidth={1.5}
									widthAndHeight={16}
									xMark={true}
								/>
							</div>
						) : (
							<div className={css(styleSheet.fileAttachmentContainer)} key={i}>
								<span className={css(styleSheet.fileAttachmentName)}>{file.name}</span>
								<DeprecatedCloseButton
									fillColor={oldSilver}
									onClick={() => onRemoveAttachmentFile(file)}
									opacity={0.9}
									outlineColor='#FFF'
									outlineWidth={1.5}
									widthAndHeight={16}
									xMark={false}
								/>
							</div>
						);
					})}
				</div>
			) : null}
			<OutboundTextMessageBubble backgroundColor={brandPrimary}>
				If you&apos;d prefer not to hear from me by text message, please reply back with &quot;Stop&quot;.
			</OutboundTextMessageBubble>
			<div className={css(styleSheet.canSpamDisclaimer)}>*Required by CAN-SPAM</div>
		</>
	);
}
